import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { TextInput, EmailInput, Button, FakeLink } from '@figure/frontlib-gui';
import { PageHeader } from 'shared/components/Typography';
import { BYPASS, WAITING } from 'shared/constants/voe';
import {
  useLoader,
  usePersonalLoan,
  usePersonalLoanRouteFsm,
  useTelemetry,
  useTimeout,
  useRedirect,
} from 'shared/hooks';

const ConfirmEmailInput = styled(EmailInput)`
  margin-bottom: 16px;
`;

const EmployerVerification = () => {
  const { sendEmployerVerification, uuid: personalLoanUuid, personalLoan } = usePersonalLoan();
  const runRouter = usePersonalLoanRouteFsm();
  const { telemetry } = useTelemetry();
  const { redirectToTimeout } = useRedirect();
  const { t } = useTranslation();
  const companySingleName = t('companySingleName');
  const bypassVoeReview = personalLoan.status?.bypassVoeReview;

  useLoader('Loading...', bypassVoeReview === WAITING);

  useTimeout(() => {
    if (bypassVoeReview === WAITING) redirectToTimeout();
  }, 20000);

  useEffect(() => {
    if (bypassVoeReview === BYPASS) runRouter();
  }, [bypassVoeReview, runRouter]);

  const handleSubmit = async ({ employerName, employeeEmail }) => {
    await sendEmployerVerification({ plUuid: personalLoanUuid, employerName, employeeEmail });
    telemetry('plVerifyEmployer');
    runRouter();
  };

  return (
    <>
      <PageHeader>
        {companySingleName} is required to verify your employment to approve your application.
      </PageHeader>
      <h3>
        Please enter your employee email address and employer name below. You will receive an email
        at the employee email address provided with further instructions.
      </h3>

      <h3>
        If you do not have an employee email,
        <FakeLink onClick={() => handleSubmit({ employerName: '', employeeEmail: '' })}>
          click here.
        </FakeLink>
      </h3>

      <Formik
        initialValues={{
          employerName: '',
          employeeEmail: '',
          confirmEmployeeEmail: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          employerName: Yup.string().required('Required'),
          employeeEmail: Yup.string().required('Required').email('Invalid'),
          confirmEmployeeEmail: Yup.string()
            .required('Required')
            .email('Invalid')
            .test('is-same', 'Email addresses must match', function (value) {
              return value === this.parent.employeeEmail;
            }),
        })}
      >
        {({ values, errors, touched, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <TextInput
              id="employer-name"
              data-test-id="employer-name"
              label="Employer Name"
              name="employerName"
              value={values.employerName}
              errorText={touched.employerName && errors.employerName}
              onChange={handleChange}
            />

            <EmailInput
              id="employee-email"
              data-test-id="employee-email"
              label="Employee Email"
              name="employeeEmail"
              value={values.employeeEmail}
              errorText={touched.employeeEmail && errors.employeeEmail}
              onChange={handleChange}
            />

            <ConfirmEmailInput
              id="confirm-employee-email"
              data-test-id="employee-email-confirm"
              label="Confirm Employee Email"
              name="confirmEmployeeEmail"
              value={values.confirmEmployeeEmail}
              errorText={touched.confirmEmployeeEmail && errors.confirmEmployeeEmail}
              onChange={handleChange}
            />

            <Button type="submit" primary large center>
              Continue
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EmployerVerification;
